import React, { useEffect, useState } from 'react';
import StaffScanGate from '@/components/StaffScanGate';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Ruler, Loader2, Check, Search,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow }
  from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const QtyInspectionStation = () => {
  const navigate = useNavigate();
  const [filterConsignment, setFilterConsignment] = useState('');
  const [bundles, setBundles] = useState([]);
  const [editing, setEditing] = useState({});  // bundle_code → actual_qty draft
  const [submitting, setSubmitting] = useState(null);

  const load = async () => {
    const params = { status: 'RECEIVED' };
    if (filterConsignment) params.consignment_id = filterConsignment;
    const r = await apiClient.get('/shirt-mfg/shirting-bundles', { params });
    setBundles(r.data.bundles || []);
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line

  const onInspect = async (b) => {
    const actual = parseFloat(editing[b.bundle_code]);
    if (isNaN(actual) || actual < 0) {
      toast.error('Enter actual qty');
      return;
    }
    setSubmitting(b.bundle_code);
    try {
      const res = await apiClient.post(
        `/shirt-mfg/shirting-bundles/${b.bundle_code}/inspect`,
        { actual_qty: actual },
      );
      toast.success(
        `Inspected — variance ${res.data.variance >= 0 ? '+' : ''}${res.data.variance}`,
      );
      setEditing((prev) => {
        const copy = { ...prev };
        delete copy[b.bundle_code];
        return copy;
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Inspect failed');
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <StaffScanGate stationName="Shirt Mfg — Qty Inspection">
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="shirt-mfg-qty-inspection">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/sections/shirt-manufacturing')}
            className="text-[#F9FAFB] hover:bg-white/10"
            data-testid="qty-inspection-back"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold font-display">Qty Inspection</h1>
            <p className="text-sm text-[#E8DCC8]">
              Record actual qty for received shirting bundles
            </p>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-base">Bundles to Inspect</CardTitle>
              <div className="flex gap-2">
                <Input
                  value={filterConsignment}
                  onChange={(e) => setFilterConsignment(e.target.value)}
                  placeholder="Filter by consignment…"
                  className="w-64"
                  data-testid="qty-inspection-filter-input"
                />
                <Button
                  variant="outline" size="sm" onClick={load}
                  data-testid="qty-inspection-refresh-btn"
                >
                  <Search className="w-4 h-4 mr-1" /> Refresh
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Bundle</TableHead>
                  <TableHead>Design</TableHead>
                  <TableHead>Shade</TableHead>
                  <TableHead>Declared</TableHead>
                  <TableHead>Actual (enter)</TableHead>
                  <TableHead className="text-right">Inspect</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!bundles.length && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-gray-400 py-8">
                      No RECEIVED bundles awaiting inspection
                    </TableCell>
                  </TableRow>
                )}
                {bundles.map((b) => (
                  <TableRow key={b.bundle_code} data-testid={`qty-insp-row-${b.bundle_code}`}>
                    <TableCell className="font-mono text-xs">{b.bundle_code}</TableCell>
                    <TableCell className="text-xs">{b.design_name}</TableCell>
                    <TableCell className="text-xs">{b.shade}</TableCell>
                    <TableCell>{b.declared_qty}</TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={editing[b.bundle_code] ?? ''}
                        onChange={(e) =>
                          setEditing({ ...editing, [b.bundle_code]: e.target.value })
                        }
                        placeholder="Actual…"
                        className="w-28"
                        data-testid={`qty-insp-actual-${b.bundle_code}`}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        onClick={() => onInspect(b)}
                        disabled={submitting === b.bundle_code || !editing[b.bundle_code]}
                        className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                        data-testid={`qty-insp-submit-${b.bundle_code}`}
                      >
                        {submitting === b.bundle_code
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <Ruler className="w-4 h-4" />}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>
    </div>
    </StaffScanGate>
  );
};

export default QtyInspectionStation;
